import {
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Typography,
  Checkbox,
  TextareaAutosize,
  styled,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CustomerModel } from "../../../../models/CustomerModels";
import { useUpdateCustomer } from "../../../../hooks/useUpdateCustomer";
import { useToggle } from "../../../../hooks/useToggle";
import { useChasable } from "../../../../hooks/useChasable";
import { getSeverityColor } from "../../../../services/helpers";

type Props = {
  customer: CustomerModel;
};

const Textarea = styled(TextareaAutosize)(({ theme }) => ({
  width: "100%",
  minWidth: "100%",
  maxWidth: "100%",
  borderRadius: "4px",
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.grey[400]}`,
  fontSize: "14px",

  "&:hover, &:focus": {
    borderColor: theme.palette.primary.main,
  },
}));

const DESCRIPTION_MAX_LENGTH = 10 * 1000;

export function EditCustomerInfoButton({ customer }: Props) {
  const { updateCustomer } = useUpdateCustomer(customer.id);
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { refetchChasable } = useChasable(id, !!searchParams.get("isFromSearch"));
  const [loading, startLoading, endLoading] = useToggle(false);
  const [open, openDialog, closeDialog] = useToggle(false);
  const defaultIsProductOrderRequired = customer.isProductOrderRequired;
  const [isProductOrderRequired, setIsProductOrderRequired] = useState<boolean | undefined>(
    defaultIsProductOrderRequired
  );
  const defaultDescription = customer.description ?? "";
  const [description, setDescription] = useState(defaultDescription);
  const charactersLeft = DESCRIPTION_MAX_LENGTH - description.length;
  const reachLimit = description.length >= DESCRIPTION_MAX_LENGTH;
  const notChanged = defaultIsProductOrderRequired === isProductOrderRequired && defaultDescription === description;

  const onCheckboxChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsProductOrderRequired(checked);
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const onConfirm = async () => {
    try {
      startLoading();
      await updateCustomer({
        isProductOrderRequired,
        description: description.trim(),
      });
      await refetchChasable();
      closeDialog();
    } catch (e) {
      console.error(e);
    } finally {
      endLoading();
    }
  };

  useEffect(() => {
    if (!open) {
      setIsProductOrderRequired(defaultIsProductOrderRequired);
      setDescription(defaultDescription);
    }
  }, [open, defaultIsProductOrderRequired, defaultDescription]);

  return (
    <>
      <IconButton aria-label="edit" color="primary" onClick={openDialog} sx={{ fontSize: "14px" }}>
        <EditIcon fontSize="inherit" />
      </IconButton>
      <Dialog open={open} onClose={closeDialog} fullScreen={true}>
        <DialogTitle>
          <Typography variant="h6" fontSize="18px">
            Edit customer
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container={true} alignItems="center" justifyContent="space-between" mb={1}>
            <Typography fontSize="14px">Product order required</Typography>
            <Checkbox size="small" color="primary" checked={isProductOrderRequired} onChange={onCheckboxChange} />
          </Grid>
          <Grid container={true} direction="column">
            <Typography fontSize="14px" mb={1}>
              Description
            </Typography>
            <Textarea
              aria-label="customer description"
              placeholder="Provide description..."
              value={description}
              onChange={onDescriptionChange}
              sx={{ overflowY: "auto !important", fontSize: "12px" }}
              minRows={15}
              maxRows={20}
              maxLength={DESCRIPTION_MAX_LENGTH}
            />
            <Grid alignSelf="end" mt={0.5} container={true} alignItems="center">
              {reachLimit && (
                <ErrorOutlineIcon sx={{ fontSize: "14px", color: getSeverityColor("warning"), marginRight: "4px" }} />
              )}
              <Typography fontSize="12px" color={reachLimit ? getSeverityColor("warning") : "text.secondary"}>
                {charactersLeft.toLocaleString()} characters remaining
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" disabled={loading || notChanged} onClick={onConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
